import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LinkOutlined } from '@ant-design/icons';
import RobotIcon from './RobotIcon';
import TypingIndicator from './TypingIndicator';
import InlineOptions from './InlineOptions';
import settingsService from '../../services/settingsService';

const isSafeUrl = (url) => {
  if (!url) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith('/')) return true;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const MessageBubble = ({ message }) => {
  const isFromMe = message.type === 'user';

  const avatarClass = isFromMe ? 'customer' : 'bot';
  const bubbleClass = isFromMe ? 'from-me' : 'from-other';

  return (
    <motion.div
      className={`chat-bubble-row ${bubbleClass}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <div className="chat-bubble-wrapper">
        <div className={`chat-avatar ${avatarClass}`}>
          {isFromMe ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#3FB5DE">
              <circle cx="12" cy="8" r="5" />
              <path d="M3 22c0-5 4-9 9-9s9 4 9 9z" />
            </svg>
          ) : (
            <RobotIcon size={24} />
          )}
        </div>

        <div className={`chat-bubble ${bubbleClass}`}>
          <div className="chat-bubble-content">
            {isFromMe ? (
              message.content
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'br', 'h1', 'h2', 'h3', 'code', 'pre', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img']}
                components={{
                  table: ({ children }) => (
                    <div className="chat-table-wrapper">
                      <table>{children}</table>
                    </div>
                  ),
                  a: ({ href, children }) => (
                    isSafeUrl(href) ? (
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ) : (
                      <span>{children}</span>
                    )
                  ),
                  img: ({ src, alt }) => (
                    <img
                      src={src}
                      alt={alt || ''}
                      style={{ maxWidth: '100%', borderRadius: '8px', marginTop: '8px', marginBottom: '8px' }}
                      loading="lazy"
                    />
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>

          {message.redirectUrl && isSafeUrl(message.redirectUrl) && (
            <a
              href={message.redirectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-bubble-link"
            >
              <LinkOutlined /> Sayfaya Git
            </a>
          )}

          <div className="chat-bubble-footer">
            <span className="chat-bubble-time">
              {new Date(message.timestamp).toLocaleTimeString('tr-TR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ChatMessages = ({
  messages,
  isLoading,
  latestOptionSetId,
  onSelectOption,
  compareMode,
  selectedCards,
  onCardToggle,
  onCompare,
  checklistMode,
  selectedCriteria,
  onCriteriaToggle,
  onRecommend,
  wizardMode,
  wizardSteps,
  currentStepIndex,
  wizardSelections,
  onWizardToggle,
  onWizardNext,
  onWizardPrev,
  onWizardSubmit,
}) => {
  const messagesContainerRef = useRef(null);
  const prevMessageCountRef = useRef(messages.length);
  const [disclaimer, setDisclaimer] = useState('');

  // Bilgilendirme notu Settings'ten gelir (panelden duzenlenebilir).
  // Kayit yoksa ya da istek basarisiz olursa not hic gosterilmez —
  // sohbetin acilmasini engellemez.
  useEffect(() => {
    let cancelled = false;
    settingsService
      .getSettingValue('DISCLAIMER_TEXT')
      .then((text) => {
        if (!cancelled && typeof text === 'string') setDisclaimer(text.trim());
      })
      .catch(() => {
        /* not gosterilmez */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Yeni mesaj eklendiginde scroll
  useEffect(() => {
    if (messages.length <= 2) return;
    if (messages.length > prevMessageCountRef.current) {
      const container = messagesContainerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  // TypingIndicator gosterildiginde scroll (indicator yuksekligi eklenince alta in)
  useEffect(() => {
    if (isLoading) {
      const container = messagesContainerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
    }
  }, [isLoading]);

  return (
    <div className="chat-messages" ref={messagesContainerRef}>
      {disclaimer && <div className="chat-disclaimer">{disclaimer}</div>}

      {messages.map((message, index) => (
        <div key={message.id}>
          {message.type === 'options' ? (
            <InlineOptions
              options={message.options}
              isPast={message.optionSetId !== latestOptionSetId}
              onSelect={onSelectOption}
              isLoading={isLoading}
              compareMode={compareMode}
              selectedCards={selectedCards}
              onCardToggle={onCardToggle}
              onCompare={onCompare}
              checklistMode={checklistMode}
              selectedCriteria={selectedCriteria}
              onCriteriaToggle={onCriteriaToggle}
              onRecommend={onRecommend}
              wizardMode={wizardMode}
              wizardSteps={wizardSteps}
              currentStepIndex={currentStepIndex}
              wizardSelections={wizardSelections}
              onWizardToggle={onWizardToggle}
              onWizardNext={onWizardNext}
              onWizardPrev={onWizardPrev}
              onWizardSubmit={onWizardSubmit}
            />
          ) : (
            <MessageBubble message={message} />
          )}
        </div>
      ))}
      {isLoading && <TypingIndicator />}
    </div>
  );
};

export default ChatMessages;
